/**
 * Meeting room availability fetching with polling.
 * Step 1: POST /api_ats/v1/meeting_rooms/trigger_fetch → returns { fetch_uuid }
 * Step 2: GET  /api_ats/v1/meeting_rooms?fetch_uuid=... → poll until rooms ready
 *
 * Used to show room availability (Available/Busy) in the meeting room picker.
 * Polling logic will be refined — keeping the hook interface stable.
 */
import { useCallback, useRef, useState } from 'react';
import { http } from '@/lib/ats-https';
import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';

export interface MeetingRoomParams {
  startTime: string;
  endTime: string;
  timezone: string;
}

export interface MeetingRoom {
  id: number;
  name: string;
  email?: string;
  capacity?: number;
  office_name?: string;
  office_id?: number;
  available?: boolean;
  [key: string]: unknown;
}

interface MeetingRoomsState {
  rooms: MeetingRoom[];
  isLoading: boolean;
  error: Error | null;
}

const POLL_INTERVAL = 2000;
const MAX_POLLS = 10;

async function triggerFetch(params: MeetingRoomParams): Promise<string> {
  const res = await http.post('/meeting_rooms/trigger_fetch', {
    start_time: params.startTime,
    end_time: params.endTime,
    time_zone: params.timezone,
  });
  assertResolvedAtsNotHttpError(res);
  const data = (res as { data?: { fetch_uuid?: string } })?.data ?? res;
  return (data as { fetch_uuid?: string }).fetch_uuid ?? '';
}

/** Poll envelope for `/meeting_rooms?fetch_uuid=...`. The backend
 *  returns an async status plus a rooms map keyed by office name; we
 *  flatten it into a flat `MeetingRoom[]` carrying `office_name`. */
interface PollRoomsEnvelope {
  data?: {
    status?: 'PENDING' | 'SUCCESS' | 'FAILED';
    results?: {
      rooms?: Record<string, MeetingRoom[]> | MeetingRoom[];
    };
  };
}

interface PollRoomsResult {
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  rooms: MeetingRoom[];
}

async function pollRooms(fetchUuid: string): Promise<PollRoomsResult> {
  const res = await http.get('/meeting_rooms', {
    params: { fetch_uuid: fetchUuid },
  });
  assertResolvedAtsNotHttpError(res);

  const envelope = res as PollRoomsEnvelope;
  const status = envelope.data?.status ?? 'PENDING';
  const raw = envelope.data?.results?.rooms;

  if (Array.isArray(raw)) {
    return { status, rooms: raw };
  }
  if (raw && typeof raw === 'object') {
    const flat: MeetingRoom[] = [];
    for (const [officeName, list] of Object.entries(raw)) {
      if (!Array.isArray(list)) continue;
      for (const room of list) {
        flat.push({
          ...room,
          // Respect explicit office_name when present; otherwise use
          // the map key (backend's office grouping label).
          office_name: room.office_name ?? officeName,
          // Backend uses `room_name`; normalise to `name` for the UI layer.
          name: room.name ?? (room as { room_name?: string }).room_name ?? '',
          email:
            room.email ?? (room as { room_email?: string }).room_email ?? '',
        });
      }
    }
    return { status, rooms: flat };
  }
  return { status, rooms: [] };
}

/**
 * Hook for fetching meeting room availability with trigger + poll pattern.
 * Call `fetchRooms(params)` to start; rooms update as polling completes.
 */
export function useMeetingRooms() {
  const [state, setState] = useState<MeetingRoomsState>({
    rooms: [],
    isLoading: false,
    error: null,
  });
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchRooms = useCallback(
    async (params: MeetingRoomParams) => {
      cleanup();
      setState({ rooms: [], isLoading: true, error: null });

      try {
        const fetchUuid = await triggerFetch(params);
        if (!fetchUuid) {
          setState((prev) => ({ ...prev, isLoading: false }));
          return;
        }

        let attempts = 0;
        const poll = async () => {
          try {
            const { status, rooms } = await pollRooms(fetchUuid);
            const finished =
              status === 'SUCCESS' ||
              status === 'FAILED' ||
              attempts >= MAX_POLLS;
            if (finished) {
              setState({ rooms, isLoading: false, error: null });
              return;
            }
            attempts++;
            pollRef.current = setTimeout(poll, POLL_INTERVAL);
          } catch (err) {
            setState({
              rooms: [],
              isLoading: false,
              error: err instanceof Error ? err : new Error(String(err)),
            });
          }
        };

        await poll();
      } catch (err) {
        setState({
          rooms: [],
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    },
    [cleanup]
  );

  return {
    ...state,
    fetchRooms,
    cleanup,
  };
}
